import React from 'react';
import { Tooltip, IconButton } from '@mui/material';
import { Share as ShareIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';

interface ShareActionProps {
  item: any;
  size?: 'small' | 'medium' | 'large';
  sx?: any;
  onShare?: (url: string) => void;
}

export default function ShareAction({ item, size = 'small', sx = {}, onShare }: ShareActionProps) {
  const { t } = useTranslation();

  const handleShare = async () => {
    try {
      const baseUrl = window.location.origin;
      const url = item.is_product ? `${baseUrl}/product/${item.id}` : `${baseUrl}/item/${item.id}`;

      // Call the onShare callback if provided
      onShare?.(url);

      // Try to use Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: item.name || item.title || 'D2RS Item',
          text: `Check out this item: ${item.name || item.title || 'D2RS Item'}`,
          url: url,
        });
        toast.success(t('search.vendor.itemShared', 'Item shared successfully'));
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(url);
        toast.success(t('common.copiedToClipboard'));
      }
    } catch (error) {
      console.error('Error sharing item:', error);

      // Final fallback - try to copy to clipboard
      try {
        const baseUrl = window.location.origin;
        const url = item.is_product
          ? `${baseUrl}/product/${item.id}`
          : `${baseUrl}/item/${item.id}`;
        await navigator.clipboard.writeText(url);
        toast.success(t('common.copiedToClipboard'));
      } catch (clipboardError) {
        console.error('Clipboard error:', clipboardError);
        toast.error(t('common.clipboardError'));
      }
    }
  };

  return (
    <Tooltip title={t('common.share')}>
      <IconButton
        onClick={handleShare}
        size={size}
        sx={{
          color: (theme) => theme.palette.text.secondary,
          border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
          '&:hover': {
            color: (theme) => theme.palette.text.primary,
            border: (theme) => `1px solid ${theme.palette.secondary.main}`,
          },
          ...sx,
        }}
      >
        <ShareIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
